const LONG_PRESS = 320;
const TOUCH_SLOP = 10;
const SYNTHETIC_CLICK_WINDOW = 800;
const RESTART_DELAY = 400;

const field = document.querySelector('#field');
const fieldSize = document.querySelector('#field-size');
const flagsNumber = document.querySelector('#flags-number');
const minesNumber = document.querySelector('#mines-number');

let game;

export default function buildField(cols, rows, mines) {
    mines = Math.min(mines, cols * rows - 9);
    game = {
        cols,
        rows,
        mines,
        opened: 0,
        flags: 0,
        over: false,
        endedAt: 0,
        cells: Array.from({length: cols * rows}, () => ({mine: false, open: false, flag: false, n: 0})),
    };

    fieldSize.textContent = `${cols}×${rows}`;
    flagsNumber.textContent = 0;
    minesNumber.textContent = mines;

    const cellElements = game.cells.map(() => Object.assign(document.createElement('div'), {className: 'cell'}));
    field.classList.remove('gameover', 'win');
    field.style.setProperty('--cols', cols);
    field.replaceChildren(...cellElements);
}

let pressTimer;
let touching = false;
let longPressed = false;
let moved = false;
let touchOrigin = {x: 0, y: 0};
let lastTouchEnd = -Infinity;

const cellIndex = e => [...field.children].indexOf(e.target.closest('.cell'));

field.addEventListener('touchstart', e => {
    clearTimeout(pressTimer);
    touching = true;
    longPressed = moved = false;
    touchOrigin = {x: e.touches[0].clientX, y: e.touches[0].clientY};
    const i = cellIndex(e);
    pressTimer = setTimeout(() => {
        longPressed = true;
        if (toggleFlag(i)) {
            ripple(field.children[i]);
            navigator.vibrate?.(15);
        }
    }, LONG_PRESS);
}, {passive: true});

field.addEventListener('touchmove', e => {
    const {clientX, clientY} = e.touches[0];
    if (Math.hypot(clientX - touchOrigin.x, clientY - touchOrigin.y) < TOUCH_SLOP) return;
    moved = true;
    clearTimeout(pressTimer);
}, {passive: true});

field.addEventListener('touchend', e => {
    clearTimeout(pressTimer);
    touching = false;
    lastTouchEnd = performance.now();
    if (moved) return;
    e.preventDefault();
    if (!longPressed) press(cellIndex(e));
});

field.addEventListener('touchcancel', () => {
    clearTimeout(pressTimer);
    touching = false;
});

field.addEventListener('click', e => {
    if (performance.now() - lastTouchEnd < SYNTHETIC_CLICK_WINDOW) return;
    press(cellIndex(e));
});

field.addEventListener('contextmenu', e => {
    e.preventDefault();
    if (!touching) toggleFlag(cellIndex(e));
});

function press(i) {
    if (game.over) return restartWhenReady();
    const cell = game.cells[i];
    if (!cell || cell.flag) return;
    if (game.opened === 0) placeMines(i);
    if (cell.open) chord(i);
    else reveal(i);
    if (!game.over && game.opened === game.cells.length - game.mines) end(true);
}

function restartWhenReady() {
    if (performance.now() - game.endedAt > RESTART_DELAY) buildField(game.cols, game.rows, game.mines);
}

function toggleFlag(i) {
    const cell = game.cells[i];
    if (game.over || !cell || cell.open) return false;
    cell.flag = !cell.flag;
    game.flags += cell.flag ? 1 : -1;
    field.children[i].classList.toggle('flag', cell.flag);
    flagsNumber.textContent = game.flags;
    return true;
}

function ripple(el) {
    const ring = Object.assign(document.createElement('span'), {className: 'ripple'});
    ring.addEventListener('animationend', () => ring.remove());
    el.append(ring);
}

function placeMines(safe) {
    const safeZone = [safe, ...neighbors(safe)];
    const spots = shuffle(game.cells.map((_, i) => i).filter(i => !safeZone.includes(i)));
    spots.slice(0, game.mines).forEach(i => game.cells[i].mine = true);
    game.cells.forEach((cell, i) => cell.n = neighbors(i).filter(j => game.cells[j].mine).length);
}

function reveal(i) {
    const queue = [i];
    while (queue.length) {
        const j = queue.pop();
        const cell = game.cells[j];
        if (cell.open || cell.flag) continue;
        cell.open = true;
        game.opened++;
        const el = field.children[j];
        el.classList.add('open');
        if (cell.mine) return explode(j);
        if (cell.n) {
            el.textContent = cell.n;
            el.style.color = `var(--n${cell.n})`;
        } else {
            queue.push(...neighbors(j));
        }
    }
}

function chord(i) {
    const around = neighbors(i);
    const flagsAround = around.filter(j => game.cells[j].flag).length;
    if (flagsAround !== game.cells[i].n) return;
    for (const j of around) {
        if (!game.over) reveal(j);
    }
}

function explode(i) {
    end(false);
    [[i], neighbors(i), neighbors(i, 2)].forEach((wave, n) =>
        wave.forEach(j => field.children[j].classList.add(`boom${n}`)));
}

function end(won) {
    game.over = true;
    game.endedAt = performance.now();
    field.classList.add('gameover');
    field.classList.toggle('win', won);
    game.cells.forEach((cell, i) => {
        if (cell.mine) field.children[i].classList.add('mine');
        if (cell.flag && !cell.mine) field.children[i].classList.add('wrong');
    });
}

function neighbors(i, distance = 1) {
    const {cols, rows} = game;
    const x = i % cols;
    const y = Math.floor(i / cols);
    const result = [];
    for (let dy = -distance; dy <= distance; dy++) {
        for (let dx = -distance; dx <= distance; dx++) {
            const nx = x + dx, ny = y + dy;
            const onRing = Math.max(Math.abs(dx), Math.abs(dy)) === distance;
            if (onRing && nx >= 0 && nx < cols && ny >= 0 && ny < rows) result.push(ny * cols + nx);
        }
    }
    return result;
}

function shuffle(array) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
}
